import Warning from '@mui/icons-material/Warning'
import React, { type FC, type ReactElement } from 'react'
import { TableRow, Tooltip } from '@mui/material'
import { TableCellBordered } from './MetersTableRow.style'
import {
  getDescTranslationKey,
  getNameTranslationKey,
  isAlarmKey,
  TEMPERATURE
} from '../../../../redux/alarm-settings/alarm-types'
import { useTranslation } from 'react-i18next'
import Check from '@mui/icons-material/Check'
import { type Alarm } from './types'
import { type DeviceAlarmSettings } from '../../../../api/device-api-types'

interface AlarmStatusIconProps {
  alarms: Alarm[]
  alarmSettings?: DeviceAlarmSettings
}

/**
 * Alarm status icon component.
 */
export const AlarmStatusIcon: FC<AlarmStatusIconProps> = ({ alarms, alarmSettings }) => {
  const enabledAlarms = getEnabledAlarms(alarms, alarmSettings)

  const atLeastOneAlarmIsActive = (): boolean => {
    return enabledAlarms.some(a => a.active)
  }

  const activeAlarms = atLeastOneAlarmIsActive()

  return (
        <Tooltip
            style={{ fontSize: '12em' }}
            arrow
            title={<AlarmStatusTooltipTitle activeAlarms={activeAlarms} enabledAlarms={enabledAlarms}/>}
        >
            {atLeastOneAlarmIsActive()
              ? (
                    <Warning style={{ marginBottom: '-5px', color: '#BE366C' }}/>
                )
              : (
                    <Check style={{ fontSize: 'large', color: '#04D08D' }}/>
                )}
        </Tooltip>
  )
}

interface AlarmDetailsProps {
  alarm: Alarm
  index: number
}

export const AlarmsDetails: FC<AlarmDetailsProps> = ({ alarm, index }) => {
  const { t } = useTranslation()
  const statusTranslationKey = getAlarmsStatusTranslationKey(alarm.supported, alarm.active)

  return (
        <TableRow
            style={{
              backgroundColor: alarm.active ? '#F7E7EE' : '#FFFFFF',
              opacity: !alarm.supported ? '0.38' : ''
            }}
        >
            <TableCellBordered>{index + 1}</TableCellBordered>
            <TableCellBordered>
                {alarm.active ? <WarningIcon/> : ''}{' '}
                <span>{t(getNameTranslationKey(alarm.type))}</span>
            </TableCellBordered>
            <TableCellBordered>
                {t(getDescTranslationKey(alarm.type))}
            </TableCellBordered>
            <TableCellBordered>
                {t(statusTranslationKey)}
            </TableCellBordered>
        </TableRow>
  )
}

/* Child-components */

function WarningIcon(): ReactElement {
  return (<Warning style={{ marginBottom: '-5px' }}/>)
}

interface AlarmStatusTooltipProps {
  enabledAlarms: Alarm[]
  activeAlarms: boolean
}

function AlarmStatusTooltipTitle({ enabledAlarms, activeAlarms }: Readonly<AlarmStatusTooltipProps>): ReactElement {
  const { t } = useTranslation()

  if (!activeAlarms) {
    return (<span>{t('alarms.noAlarms')}</span>)
  }

  const createNewAlarmStatusLine = (a: Alarm): string => {
    return (
      t(getNameTranslationKey(a.type)) +
            ':\n' +
            t(getDescTranslationKey(a.type)) +
            '\n\n'
    )
  }
  let alarmStatus = ''
  enabledAlarms.forEach(
    (a) =>
      a.active && a.supported && (alarmStatus += createNewAlarmStatusLine(a))
  )

  return (<span style={{ whiteSpace: 'pre-line' }}>{alarmStatus}</span>)
}

/* Helper Functions */

function getAlarmsStatusTranslationKey(supported: boolean, active: boolean): string {
  if (supported) {
    if (active) {
      return 'alarms.status.active'
    } else {
      return 'alarms.status.cleared'
    }
  }
  return 'alarms.notSupported'
}

function getEnabledAlarms(alarms: Alarm[], alarmSettings?: DeviceAlarmSettings): Alarm[] {
  const enabledAlarms: Alarm[] = []

  alarms.forEach((a) => {
    if (a.type !== undefined && isAlarmKey(a.type.alarmKey) && isAlarmEnabled(a, alarmSettings)) {
      enabledAlarms.push(a)
    }
  })

  return enabledAlarms
}

function isAlarmEnabled(alarm: Alarm, alarmSettings?: DeviceAlarmSettings): boolean {
  if (alarm.type === undefined) {
    return false
  }

  if (alarm.type.alarmKey === TEMPERATURE.alarmKey) {
    return true
  }

  if (alarmSettings == null) {
    return false
  }

  return alarmSettings[alarm.type.enabledKey]
}
